import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SettingsService } from 'src/app/settings/settings.service';

interface FacturaCartera {
  id: number;              // cartera_vigente_id — se envía en el POST de aplicar
  docto: string;           // folio que ve el cliente
  operacion: string;
  deudor: string;
  diasVencido: number;     // signo: negativo = por vencer
  deudaNeta: number;
  interesMora: number;
  total: number;           // deuda_neta + interes_mora — monto exigible
  minimo: number;          // monto mínimo aceptado por cobranza
  pagable: boolean;
  pagada: boolean;
  saldoPendiente: number;  // se actualiza con lo que devuelve el backend tras pagar
  needGestion: boolean;    // "permitir que cobranza siga gestionando esta factura"
  // Lo que se paga es una factura o una cuota de crédito: son productos distintos, con
  // lenguaje distinto, y nunca se muestran en la misma lista.
  tipo: 'factura' | 'cuota';
  credito?: number;        // número de crédito que conoce el cliente (solo cuotas)
  numero?: number;         // número de cuota (solo cuotas)
  numCuotas?: number;      // total de cuotas del crédito, para "cuota 2 de 4"
}

interface CreditoCartera {
  credito: number;
  numCuotas: number;
  deudaTotal: number;
  cuotas: FacturaCartera[];
}

interface CuentaBancaria {
  id: number;
  bank: { name: string };
  tipo_cuenta: string;
  number: string;
  name: string;
  is_primary: boolean;
}

type Grupo = 'vencida' | 'por-vencer';
type Paso = 'que-pagar' | 'cartera' | 'creditos' | 'cuanto' | 'procesando' | 'resultado'
  | 'giro-monto' | 'giro-confirmar' | 'giro-procesando' | 'giro-resultado';
type DrawerEstado = 'datos' | 'comprobante' | 'facturas' | 'monto' | 'loader' | 'verificacion';

@Component({
  selector: 'app-disponible',
  templateUrl: './disponible.component.html',
  styleUrls: ['./disponible.component.less'],
  standalone: false
})
export class DisponibleComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(private router: Router, private route: ActivatedRoute, public settings: SettingsService) {}

  @ViewChild('drawerRoot') drawerRoot!: ElementRef<HTMLElement>;

  private companyId = localStorage.getItem('company_id');

  async ngOnInit() {
    const entry = (this.route.snapshot.data['entry'] as Paso) || 'que-pagar';
    this.paso = entry;
    await this.cargarCartera();
    if (entry === 'giro-monto') {
      this.cargarCuentasBancarias();
    }
  }

  ngAfterViewInit() {
    // El header de la app (layout.component.less) crea su propio contexto
    // de apilamiento con z-index:1000 !important, así que ningún z-index
    // dentro del árbol del componente puede superarlo. Se mueve el drawer
    // directamente a <body> para escapar de ese contexto.
    document.body.appendChild(this.drawerRoot.nativeElement);
  }

  ngOnDestroy() {
    this.drawerRoot?.nativeElement?.remove();
  }

  paso: Paso = 'que-pagar';

  disponible: number = 0;
  facturas: FacturaCartera[] = [];
  creditos: CreditoCartera[] = [];
  cargandoCartera: boolean = false;
  errorCartera: string | null = null;

  grupos: { key: Grupo; label: string; colorClass: string }[] = [
    { key: 'vencida', label: 'Vencidas', colorClass: 'rc-group-mora' },
    { key: 'por-vencer', label: 'Por vencer', colorClass: 'rc-group-vencer' },
  ];

  facturaSeleccionada: FacturaCartera | null = null;

  montoInput: string = '';
  opcionSeleccionada: 'total' | 'mora' | 'abono' | null = null;
  montoError: string = '';
  montoAPagar: number = 0;
  esTotal: boolean = false;

  private procesandoEnCurso: boolean = false;

  // ─── Drawer: Pagar por transferencia ───
  // TODO: no hay endpoint documentado todavía para el flujo de comprobante,
  // se mantiene simulado (igual que antes).
  drawerAbierto: boolean = false;
  drawerEstado: DrawerEstado = 'datos';
  drawerCopiado: boolean = false;
  drawerArchivoSubido: boolean = false;
  drawerArchivoNombre: string = '';
  drawerFacturaSeleccionada: FacturaCartera | null = null;
  drawerOpcion: 'total' | 'abono' | null = null;
  drawerMontoInput: string = '';
  drawerMontoError: string = '';

  private readonly DRAWER_BACK: Partial<Record<DrawerEstado, DrawerEstado>> = {
    comprobante: 'datos',
    facturas: 'comprobante',
    monto: 'facturas',
  };

  fmt(n: number): string {
    if (!n) return '$0';
    return '$' + this.settings.formatMoney(Math.round(n), 0);
  }

  private formatMiles(n: number): string {
    return this.settings.formatMoney(n, 0);
  }

  // ─── Navegación entre pasos ───
  pStep(paso: Paso) {
    this.paso = paso;
    window.scrollTo(0, 0);
  }

  irAQuePagar() {
    this.facturaSeleccionada = null;
    this.pStep('que-pagar');
  }

  volverPortal() {
    this.router.navigate(['/home']);
  }

  // Botón "Volver" visible en el contenido (no solo el breadcrumb de arriba,
  // que quedaba como único medio de navegación y poco evidente como tal).
  private readonly PASO_ANTERIOR: Partial<Record<Paso, Paso>> = {
    cartera: 'que-pagar',
    creditos: 'que-pagar',
    cuanto: 'cartera',
    'giro-monto': 'que-pagar',
    'giro-confirmar': 'giro-monto',
  };

  tieneVolver(): boolean {
    return this.paso in this.PASO_ANTERIOR;
  }

  volverAtras() {
    // Desde el monto se vuelve a la lista del producto que se estaba pagando.
    const anterior = this.paso === 'cuanto' && this.esCuota() ? 'creditos' : this.PASO_ANTERIOR[this.paso];
    if (!anterior) return;
    if (anterior === 'que-pagar') {
      this.irAQuePagar();
    } else {
      this.pStep(anterior);
    }
  }

  // ─── Cartera (GET /cliente/pagos/cartera/<company_id>/) ───
  async cargarCartera() {
    this.cargandoCartera = true;
    this.errorCartera = null;

    try {
      const bd: any = await this.settings.get_query(1, `cliente/pagos/cartera/${this.companyId}/`);
      const info = bd._body?.success?.info;
      this.disponible = info?.disponible ?? this.disponible;
      this.facturas = (info?.facturas ?? []).map((f: any): FacturaCartera => ({
        id: f.id,
        docto: String(f.docto),
        operacion: String(f.operacion),
        deudor: f.deudor,
        diasVencido: f.dias_vencido,
        deudaNeta: f.deuda_neta,
        interesMora: f.interes_mora,
        total: f.total,
        minimo: f.minimo,
        pagable: f.pagable,
        pagada: false,
        saldoPendiente: f.total,
        needGestion: !!f.need_gestion,
        tipo: 'factura',
      }));
      this.creditos = (info?.creditos ?? []).map((c: any): CreditoCartera => ({
        credito: c.credito,
        numCuotas: c.num_cuotas,
        deudaTotal: c.deuda_total,
        cuotas: (c.cuotas ?? []).map((q: any): FacturaCartera => ({
          id: q.id,
          docto: '',
          operacion: '',
          deudor: '',
          diasVencido: q.dias_vencido,
          deudaNeta: q.deuda_neta,
          interesMora: q.interes_mora,
          total: q.total,
          minimo: q.minimo,
          pagable: q.pagable,
          pagada: false,
          saldoPendiente: q.total,
          needGestion: false,
          tipo: 'cuota',
          credito: c.credito,
          numero: q.numero,
          numCuotas: c.num_cuotas,
        })),
      }));
    } catch (bd: any) {
      this.errorCartera = 'No pudimos cargar tu cartera. Intenta nuevamente.';
    } finally {
      this.cargandoCartera = false;
    }
  }

  pendientes(): FacturaCartera[] {
    return this.facturas.filter(f => !f.pagada);
  }

  cuotasPendientes(c: CreditoCartera): FacturaCartera[] {
    return c.cuotas.filter(q => !q.pagada);
  }

  creditosPendientes(): CreditoCartera[] {
    return this.creditos.filter(c => this.cuotasPendientes(c).length > 0);
  }

  // Lo que queda por pagar del mismo producto que se acaba de pagar.
  quedanPendientes(tipo: 'factura' | 'cuota'): boolean {
    return tipo === 'cuota' ? this.creditosPendientes().length > 0 : this.pendientes().length > 0;
  }

  esCuota(): boolean {
    return this.facturaSeleccionada?.tipo === 'cuota';
  }

  // Sin un campo de estado/grupo desde el backend, se agrupa por si la
  // factura ya venció o está por vencer (dias_vencido con signo). El texto
  // de código interno de cobranza no se muestra crudo (la doc de la API
  // deja "sin definir" ese mapeo) — mientras tanto se usan días.
  facturasPorGrupo(key: Grupo): FacturaCartera[] {
    return this.pendientes().filter(f => (f.diasVencido >= 0) === (key === 'vencida'));
  }

  badgeClass(f: FacturaCartera): string {
    return f.diasVencido >= 0 ? 'badge-mora' : 'badge-vencer';
  }

  badgeTexto(f: FacturaCartera): string {
    if (f.diasVencido >= 0) {
      return f.diasVencido === 0 ? 'Vence hoy' : `Vencida hace ${f.diasVencido} día${f.diasVencido !== 1 ? 's' : ''}`;
    }
    const dias = Math.abs(f.diasVencido);
    return `Vence en ${dias} día${dias !== 1 ? 's' : ''}`;
  }

  deudaTotal(): number {
    return this.pendientes().reduce((sum, f) => sum + f.total, 0);
  }

  seleccionarFactura(f: FacturaCartera) {
    this.facturaSeleccionada = this.facturaSeleccionada?.id === f.id ? null : f;
  }

  continuarDesdeCartera() {
    if (!this.facturaSeleccionada) return;
    this.montoInput = '';
    this.opcionSeleccionada = null;
    this.montoError = '';
    this.needGestionSeleccionado = this.facturaSeleccionada.needGestion;
    this.needGestionError = '';
    this.pStep('cuanto');
  }


  seleccionarOpcionMonto(op: 'total' | 'mora' | 'abono') {
    const f = this.facturaSeleccionada;
    if (!f) return;
    if (op !== 'abono' && this.disponibleNoCubreMora()) return;

    if (op === 'mora') {
      // Exactamente la mora: es lo mínimo que acepta cobranza.
      this.montoInput = this.formatMiles(f.minimo);
      this.opcionSeleccionada = 'mora';
      this.montoError = '';
      this.sincronizarNeedGestionSeleccion();
      return;
    }

    if (op === 'total') {
      // Mismo cap que onMontoInput(): si el disponible no cubre el total, este
      // botón llena con lo máximo que se puede pagar -no con el total real de
      // la factura-, y por eso la opción resultante es "abono", no "total"
      // (deja saldo pendiente). Espejo del comportamiento de la app móvil.
      const maximo = Math.min(f.total, this.disponible);
      this.montoInput = this.formatMiles(maximo);
      this.opcionSeleccionada = maximo >= f.total ? 'total' : 'abono';
    } else {
      this.montoInput = '';
      this.opcionSeleccionada = 'abono';
    }

    this.montoError = '';
    this.sincronizarNeedGestionSeleccion();
  }

  onMontoInput(el: HTMLInputElement) {
    const f = this.facturaSeleccionada;
    if (!f) return;

    const raw = (el.value || '').replace(/[^0-9]/g, '');
    let monto = raw ? parseInt(raw, 10) : 0;

    const maximo = Math.min(f.total, this.disponible);
    if (monto > maximo) {
      monto = maximo;
    }

    // El monto escrito determina la opción, en vez de exigir que el usuario
    // elija "abono" o "total" antes de poder tipear: llegar (o pasarse) del
    // total de la factura selecciona "Pagar el total" y cualquier monto
    // menor selecciona "Realizar un abono".
    this.opcionSeleccionada = monto <= 0 ? null
      : monto >= f.total ? 'total'
      : (f.minimo > 0 && monto === f.minimo) ? 'mora'
      : 'abono';

    const formatted = monto ? this.formatMiles(monto) : '';
    el.value = formatted;
    this.montoInput = formatted;
    this.montoError = '';
    this.sincronizarNeedGestionSeleccion();
  }

  // La opción "seguir gestionando" solo tiene sentido pagando el total (si queda
  // deuda pendiente, cobranza igual la va a seguir gestionando). Al salir de esa
  // opción se descarta cualquier cambio sin confirmar, volviendo al valor real
  // que tiene la factura hoy.
  private sincronizarNeedGestionSeleccion() {
    const f = this.facturaSeleccionada;
    if (!f) return;
    if (this.opcionSeleccionada !== 'total') {
      this.needGestionSeleccionado = f.needGestion;
    }
  }

  // Con mora, lo mínimo que se puede pagar es la mora: si el disponible no alcanza, no hay
  // monto válido y la pantalla tiene que decirlo en vez de dejar el campo trabado en $0.
  disponibleNoCubreMora(): boolean {
    const f = this.facturaSeleccionada;
    return !!f && f.minimo > 0 && this.disponible < f.minimo;
  }

  montoParseado(): number {
    return parseInt((this.montoInput || '').replace(/\./g, ''), 10) || 0;
  }

  saldoDespues(): number {
    const f = this.facturaSeleccionada;
    if (!f) return 0;
    return Math.max(f.total - this.montoParseado(), 0);
  }

  disponibleDespues(): number {
    return Math.max(this.disponible - this.montoParseado(), 0);
  }

  // El doc de la API marca esto como bug conocido: "Tu cuenta quedó
  // regularizada" no debería mostrarse solo porque ESTA factura se pagó
  // entera si quedan otras abiertas.
  todoRegularizado(): boolean {
    return this.esTotal && !this.quedanPendientes(this.facturaSeleccionada?.tipo ?? 'factura');
  }

  // ─── Aplicar pago (POST /cliente/pagos/aplicar/<company_id>/) ───
  confirmarPago() {
    if (this.procesandoEnCurso) return; // guard contra doble click

    const f = this.facturaSeleccionada;
    if (!f) return;
    const monto = this.montoParseado();

    if (!monto || monto <= 0) {
      this.montoError = 'Ingresa un monto válido.';
      return;
    }
    const que = f.tipo === 'cuota' ? 'la cuota' : 'la factura';
    if (monto < f.minimo) {
      this.montoError = `El monto debe cubrir al menos la mora de ${que} (${this.fmt(f.minimo)}).`;
      return;
    }
    if (monto > f.total) {
      this.montoError = `El monto no puede superar la deuda de ${que}.`;
      return;
    }
    if (monto > this.disponible) {
      this.montoError = 'El monto no puede superar tu disponible.';
      return;
    }

    this.montoError = '';
    this.montoAPagar = monto;
    this.procesandoEnCurso = true;
    this.pStep('procesando');

    this.aplicarPago(f, monto);
  }

  private async aplicarPago(f: FacturaCartera, monto: number) {
    try {
      const bd: any = await this.settings.get_query(2, `cliente/pagos/aplicar/${this.companyId}/`, {
        cartera_vigente_id: f.id,
        monto,
      });
      const info = bd._body?.success?.info;

      this.disponible = info.disponible;
      f.saldoPendiente = info.saldo_pendiente;
      f.pagada = info.pagada;
      this.esTotal = info.pagada;

      // Se avisa recién ahora que el pago ya se aplicó -no al mover el switch-, y solo
      // si de verdad cambió: evita un POST innecesario cuando la factura ya estaba así.
      if (f.tipo === 'factura' && this.opcionSeleccionada === 'total' && this.needGestionSeleccionado !== f.needGestion) {
        await this.guardarNeedGestion(f, this.needGestionSeleccionado);
      }

      this.pStep('resultado');
    } catch (bd: any) {
      const status = bd.status;
      const backendMsg = bd.error?.success?.message as string | undefined;

      this.pStep('cuanto');

      if (status === 404) {
        this.montoError = f.tipo === 'cuota'
          ? 'La cuota ya no está disponible para pago.'
          : 'La factura ya no está disponible para pago.';
        await this.cargarCartera();
      } else if (backendMsg) {
        this.montoError = backendMsg;
        if (status === 400 && backendMsg.startsWith('Tu disponible cambió')) {
          await this.cargarCartera();
        }
      } else {
        this.montoError = 'Ocurrió un error al procesar tu pago. Intenta nuevamente.';
      }
    } finally {
      this.procesandoEnCurso = false;
    }
  }

  // ─── Gestión de cobranza (POST /cliente/pagos/need-gestion/<company_id>/) ───
  // El switch solo cambia este estado local -no llama al backend-: se aplica recién
  // al confirmar el pago (ver aplicarPago()), para no pegarle al backend cada vez que
  // el cliente prende y apaga el check antes de decidirse.
  needGestionSeleccionado: boolean = false;
  needGestionError: string = '';

  toggleNeedGestionSeleccion() {
    this.needGestionSeleccionado = !this.needGestionSeleccionado;
  }

  private async guardarNeedGestion(f: FacturaCartera, valor: boolean) {
    try {
      await this.settings.get_query(2, `cliente/pagos/need-gestion/${this.companyId}/`, {
        cartera_vigente_id: f.id,
        need_gestion: valor,
      });
      f.needGestion = valor;
    } catch (bd: any) {
      // No bloquea el resultado del pago, que ya se aplicó: solo se informa.
      this.needGestionError = 'El pago se aplicó, pero no pudimos actualizar la gestión de cobranza de esta factura.';
    }
  }

  async pagarOtra() {
    const tipo = this.facturaSeleccionada?.tipo ?? 'factura';
    this.facturaSeleccionada = null;
    // Tras un abono, `f.total` de la factura pagada parcialmente queda
    // desactualizado en memoria (aplicarPago() solo pisa f.saldoPendiente,
    // no f.total, que es lo que muestra la columna "Deuda" de esta lista).
    // Se recarga la cartera para traer los montos reales en vez de arrastrar
    // el estado local viejo.
    await this.cargarCartera();
    const lista: Paso = tipo === 'cuota' ? 'creditos' : 'cartera';
    this.pStep(this.quedanPendientes(tipo) ? lista : 'que-pagar');
  }

  // ─── Solicitar giro ───
  // TODO: no hay endpoint documentado todavía para giros — sigue simulado
  // (no descuenta disponible real en backend, solo localmente para la UX).
  cuentasBancarias: CuentaBancaria[] = [];
  cargandoCuentas: boolean = false;
  errorCuentas: string | null = null;
  cuentaSeleccionada: CuentaBancaria | null = null;

  montoGiroInput: string = '';
  girarTodo: boolean = false;
  montoGiroError: string = '';
  montoAGirar: number = 0;
  private procesandoGiroEnCurso: boolean = false;

  // Mismo endpoint y criterio de selección que CuentasBancariasComponent
  // (modals/cuentas-bancarias en mobile): preselecciona la cuenta is_primary.
  async cargarCuentasBancarias() {
    this.cargandoCuentas = true;
    this.errorCuentas = null;
    try {
      const bd: any = await this.settings.get_query(1, `cuentasAcreedoresBank/${this.companyId}/?filtro=`);
      this.cuentasBancarias = bd._body?.accounts ?? [];
      this.cuentaSeleccionada = this.cuentasBancarias.find(c => c.is_primary) || this.cuentasBancarias[0] || null;
    } catch (bd: any) {
      this.errorCuentas = 'No pudimos cargar tus cuentas bancarias.';
    } finally {
      this.cargandoCuentas = false;
    }
  }

  seleccionarCuenta(c: CuentaBancaria) {
    this.cuentaSeleccionada = c;
  }

  irAAgregarCuenta() {
    this.router.navigate(['/home/cuentas-bancarias']);
  }

  irASolicitarGiro() {
    this.facturaSeleccionada = null;
    this.montoGiroInput = '';
    this.girarTodo = false;
    this.montoGiroError = '';
    this.pStep('giro-monto');
    if (this.cuentasBancarias.length === 0 && !this.cargandoCuentas) {
      this.cargarCuentasBancarias();
    }
  }

  onMontoGiroInput(el: HTMLInputElement) {
    const raw = (el.value || '').replace(/[^0-9]/g, '');
    let monto = raw ? parseInt(raw, 10) : 0;

    if (monto > this.disponible) {
      monto = this.disponible;
    }

    const formatted = monto ? this.formatMiles(monto) : '';
    el.value = formatted;
    this.montoGiroInput = formatted;

    this.girarTodo = monto > 0 && monto === this.disponible;
    this.montoGiroError = '';
  }

  onToggleGirarTodo() {
    if (this.girarTodo) {
      this.montoGiroInput = this.formatMiles(this.disponible);
    } else {
      this.montoGiroInput = '';
    }
    this.montoGiroError = '';
  }

  montoGiroParseado(): number {
    return parseInt((this.montoGiroInput || '').replace(/\./g, ''), 10) || 0;
  }

  disponiblePostGiro(): number {
    return Math.max(this.disponible - this.montoGiroParseado(), 0);
  }

  puedeContinuarGiro(): boolean {
    const monto = this.montoGiroParseado();
    if (!monto || monto <= 0 || monto > this.disponible) return false;
    if (this.cuentasBancarias.length === 0 || !this.cuentaSeleccionada) return false;
    return true;
  }

  continuarGiro() {
    const monto = this.montoGiroParseado();
    if (!monto || monto <= 0) {
      this.montoGiroError = 'Ingresa un monto válido.';
      return;
    }
    if (monto > this.disponible) {
      this.montoGiroError = 'El monto no puede superar tu disponible.';
      return;
    }
    if (!this.cuentaSeleccionada) {
      this.montoGiroError = 'Selecciona una cuenta de destino.';
      return;
    }
    this.montoGiroError = '';
    this.montoAGirar = monto;
    this.pStep('giro-confirmar');
  }

  confirmarGiro() {
    if (this.procesandoGiroEnCurso) return; // guard contra doble click

    this.procesandoGiroEnCurso = true;
    this.pStep('giro-procesando');

    // Sin endpoint de giro documentado todavía: se simula el procesamiento
    // y se descuenta el disponible solo localmente (no queda persistido).
    setTimeout(() => {
      this.procesandoGiroEnCurso = false;
      this.disponible = Math.max(this.disponible - this.montoAGirar, 0);
      this.pStep('giro-resultado');
    }, 1800);
  }

  // ─── Drawer: Pagar por transferencia ───
  abrirDrawer() {
    this.drawerEstado = 'datos';
    this.drawerCopiado = false;
    this.drawerArchivoSubido = false;
    this.drawerArchivoNombre = '';
    this.drawerFacturaSeleccionada = null;
    this.drawerOpcion = null;
    this.drawerMontoInput = '';
    this.drawerMontoError = '';
    this.drawerAbierto = true;
  }

  cerrarDrawer() {
    this.drawerAbierto = false;
  }

  drawerVolver() {
    const prev = this.DRAWER_BACK[this.drawerEstado];
    if (prev) this.drawerEstado = prev;
  }

  drawerIrComprobante() {
    this.drawerEstado = 'comprobante';
  }

  drawerArchivoSeleccionado(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    this.drawerArchivoSubido = true;
    this.drawerArchivoNombre = file.name;
  }

  drawerIrFacturas() {
    if (!this.drawerArchivoSubido) return;
    this.drawerFacturaSeleccionada = null;
    this.drawerEstado = 'facturas';
  }

  drawerSeleccionarFactura(f: FacturaCartera) {
    this.drawerFacturaSeleccionada = this.drawerFacturaSeleccionada?.id === f.id ? null : f;
  }

  drawerIrMonto() {
    if (!this.drawerFacturaSeleccionada) return;
    this.drawerOpcion = null;
    this.drawerMontoInput = '';
    this.drawerMontoError = '';
    this.drawerEstado = 'monto';
  }

  drawerSeleccionarOpcion(op: 'total' | 'abono') {
    this.drawerOpcion = op;
    const f = this.drawerFacturaSeleccionada;
    if (!f) return;
    this.drawerMontoInput = op === 'total' ? this.formatMiles(f.total) : '';
    this.drawerMontoError = '';
  }

  drawerOnMontoInput(el: HTMLInputElement) {
    if (this.drawerOpcion === 'total' || !this.drawerFacturaSeleccionada) return;

    const raw = (el.value || '').replace(/[^0-9]/g, '');
    let monto = raw ? parseInt(raw, 10) : 0;

    const maximo = this.drawerFacturaSeleccionada.total;
    if (monto > maximo) {
      monto = maximo;
    }

    const formatted = monto ? this.formatMiles(monto) : '';
    el.value = formatted;
    this.drawerMontoInput = formatted;
    this.drawerMontoError = '';
  }

  drawerMontoParseado(): number {
    return parseInt((this.drawerMontoInput || '').replace(/\./g, ''), 10) || 0;
  }

  drawerSaldoPendiente(): number {
    if (!this.drawerFacturaSeleccionada) return 0;
    return Math.max(this.drawerFacturaSeleccionada.total - this.drawerMontoParseado(), 0);
  }

  drawerDisponiblePostPago(): number {
    return Math.max(this.disponible - this.drawerMontoParseado(), 0);
  }

  drawerPuedeConfirmar(): boolean {
    if (this.drawerOpcion === 'total') return true;
    if (this.drawerOpcion === 'abono') return this.drawerMontoParseado() > 0;
    return false;
  }

  drawerConfirmarPago() {
    const f = this.drawerFacturaSeleccionada;
    if (!f || !this.drawerOpcion) return;

    const monto = this.drawerMontoParseado();
    if (this.drawerOpcion === 'abono') {
      if (!monto || monto <= 0) {
        this.drawerMontoError = 'Ingresa un monto válido.';
        return;
      }
      if (monto > f.total) {
        this.drawerMontoError = 'El monto no puede superar la deuda total.';
        return;
      }
    }

    this.drawerMontoError = '';
    this.drawerEstado = 'loader';

    // El comprobante queda a la espera de validación manual: a diferencia
    // del flujo de "Regularizar cartera", acá no se descuenta el
    // disponible todavía, mientras el backend no lo procese.
    setTimeout(() => { this.drawerEstado = 'verificacion'; }, 1800);
  }

  drawerCopiarDatos() {
    const datos = [
      'Banco: Banco BICE',
      'N.º de cuenta corriente: 01-39940-3',
      'Razón Social: SIMPLI S.A.',
      'RUT: 76.865.845-5',
    ].join('\n');

    navigator.clipboard.writeText(datos).then(() => {
      this.drawerCopiado = true;
      setTimeout(() => { this.drawerCopiado = false; }, 2000);
    });
  }

  drawerVolverInicio() {
    this.cerrarDrawer();
    this.volverPortal();
  }
}
